import React from 'react'
import { Box, Container, Grid } from '@mui/material'
import { List } from 'immutable'
import {
  CONTENT_REFERENCE_FIELDS_EXTENDED,
  YOGA_REFERENCE_FIELDS,
  MEDITATION_REFERENCE_FIELDS,
} from 'services/content-references/types'
import { CONTENT_TYPE_VIDEO } from 'services/content/content-types'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import ContentReferenceField from './content-references/ContentReferenceField'
import { useContentReferencesSection } from './useContentReferencesSection'
import type { SectionManagedSectionProps, ContentReferencesSectionContentData } from './types'

const EXTENDED_FULLWIDTH_TYPES = (
  CONTENT_REFERENCE_FIELDS_EXTENDED.get(CONTENT_TYPE_VIDEO, List()) as List<string>
).toArray() as string[]
const yogaFieldsArray = YOGA_REFERENCE_FIELDS.toArray() as string[]
const meditationFieldsArray = MEDITATION_REFERENCE_FIELDS.toArray() as string[]

/** All reference types in this section (for state and save). */
const ALL_EXTENDED_TYPES = [
  ...EXTENDED_FULLWIDTH_TYPES,
  ...yogaFieldsArray,
  ...meditationFieldsArray,
]

export type VideoContentReferencesExtendedSectionContentData = ContentReferencesSectionContentData

export interface VideoContentReferencesExtendedSectionProps extends SectionManagedSectionProps {
  contentData: ContentReferencesSectionContentData | null
}

/**
 * Video Content References Extended Section (section-managed).
 * Full-width rows for extended types, then two-column yoga | meditation grid.
 * Uses useContentReferencesSection for state/mutations/save; renders via ContentReferenceField.
 *
 * Query dependency: This section expects contentData.contentReferencesFrom to include all types
 * it uses (see ALL_EXTENDED_TYPES above). The page's GraphQL query (GET_VIDEO_CONTENT in
 * queries.ts) must request contentReferencesFrom(types: [these types]). If the query omits them,
 * existing refs won't load and saved refs will disappear after refetch.
 */
function VideoContentReferencesExtendedSection ({
  contentData,
  isViewOnly,
  onDirtyChange,
  saveHandlerRef,
  onResetTriggered,
}: VideoContentReferencesExtendedSectionProps) {
  const { contentReferences, handleContentReferencesChange } = useContentReferencesSection({
    refTypes: ALL_EXTENDED_TYPES,
    contentData,
    onDirtyChange,
    saveHandlerRef,
    onResetTriggered,
  })

  const renderField = (referenceType: string) => (
    <ContentReferenceField
      key={referenceType}
      referenceType={referenceType}
      value={contentReferences[referenceType] || []}
      onChange={(newReferences) => handleContentReferencesChange(referenceType, newReferences)}
      disabled={isViewOnly}
    />
  )

  return (
    <Container className="content-detail__section content-references-extended" maxWidth={false}>
      <SectionAccordion title="Content References Extended" collapsed={false}>
        {EXTENDED_FULLWIDTH_TYPES.map((referenceType) => renderField(referenceType))}

        <Box sx={{ width: '100%' }}>
          {yogaFieldsArray.map((yogaField, idx) => (
            <Grid container spacing={1} alignItems="center" key={yogaField}>
              <Grid item xs={12} md={6}>
                {renderField(yogaField)}
              </Grid>
              <Grid item xs={12} md={6}>
                {meditationFieldsArray[idx] ? renderField(meditationFieldsArray[idx]) : null}
              </Grid>
            </Grid>
          ))}
        </Box>
      </SectionAccordion>
    </Container>
  )
}

export default VideoContentReferencesExtendedSection
